import { AMSAutocomplete, AMSPdfViewerDialog, AMSTable } from '../../helpers/ui';
import {
  AccessPermissions,
  ActOfAcceptanceListResponse,
  CompanyListResponse,
  LegalEntityListResponse,
  OrderTypeKeys,
  PartnerListResponse,
  UserListResponse,
  cookiesService,
  getOrderTypeLabelByKey,
  orderTypeLookups
} from '../../services';
import { Checkbox, Grid, TextField, Typography } from '@material-ui/core';
import Lookup, {
  companyToLookup,
  legalEntityToLookup,
  partnerToLookup,
  userToLookup
} from '../../models/lookup';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ignoreOffset,
  isValidDate,
  toDateString,
  toEndOfDay,
  toStartOfDay
} from '../../helpers/date-helper';
import {
  useActsOfAcceptance,
  useCompanies,
  useLegalEntities,
  usePartners,
  usePermissions,
  useStyles,
  useUsers,
  useWarehouses
} from '../../helpers/hooks';

import AMSButton from '../../helpers/ui/AMSButton/AMSButton';
import AMSLink from '../../helpers/ui/AMSLink/AMSLink';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import DescriptionIcon from '@material-ui/icons/Description';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { getActOfAcceptanceUrl } from '../../helpers/utils';
import { parseFilter } from '../../helpers/url';
import { useHistory } from 'react-router-dom';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ActsOfAcceptanceComponent = ({ location }: any) => {
  const classes = useStyles();
  const history = useHistory();
  const { fromDate, toDate, companyIds, userIds, partnerIds, legalEntityIds, typeIds } = useMemo(
    () => parseFilter(location.search),
    [location.search]
  );

  const [title, setTitle] = useState('');
  const [openActOfAcceptancePreview, setOpenActOfAcceptancePreview] = useState(false);
  const [url, setUrl] = useState('');

  const filter = useMemo(() => cookiesService.getActOfAcceptanceFilter(), []);

  const [filterUsers, setFilterUsers] = useState<Lookup[]>([]);
  const [filterCompanies, setFilterCompanies] = useState<Lookup[]>([]);
  const [filterLegalEntities, setFilterLegalEntities] = useState<Lookup[]>([]);
  const [filterPartners, setFilterPartners] = useState<Lookup[]>([]);
  const [filterType, setFilterType] = useState<Lookup[]>(
    typeIds ? typeIds : filter.filterType ? filter.filterType : []
  );
  const [filterFromDate, setFilterFromDate] = useState<Date | null>(
    fromDate
      ? fromDate
      : filter.filterFromDate
      ? ignoreOffset(new Date(filter.filterFromDate))
      : new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)
  );
  const [filterToDate, setFilterToDate] = useState<Date | null>(
    toDate ? toDate : filter.filterToDate ? ignoreOffset(new Date(filter.filterToDate)) : new Date()
  );

  const { actsOfAcceptance, loading, setParameters } = useActsOfAcceptance(
    filterFromDate,
    filterToDate,
    partnerIds ?? filterPartners.map((p: Lookup) => p.id),
    legalEntityIds ?? filterLegalEntities.map((l: Lookup) => l.id),
    companyIds ?? filterCompanies.map((c: Lookup) => c.id),
    userIds ?? filterUsers.map((u: Lookup) => u.id),
    typeIds ?? filterType.map((t: Lookup) => t.id)
  );
  const { users } = useUsers();
  const { companies } = useCompanies();
  const { legalEntities } = useLegalEntities();
  const { partners } = usePartners();
  const { warehouses } = useWarehouses();

  const usersLookup = useMemo(
    () =>
      users.reduce((res: any, user: UserListResponse) => {
        if (!res) {
          res = {};
        }
        res[`${user.id}`] = user.name;
        return res;
      }, {}),
    [users]
  );

  const companiesLookup = useMemo(
    () =>
      companies.reduce((res: any, company: CompanyListResponse) => {
        if (!res) {
          res = {};
        }
        res[`${company.id}`] = company.name;
        return res;
      }, {}),
    [companies]
  );

  const legalEntitiesLookup = useMemo(
    () =>
      legalEntities.reduce((res: any, legalEntity: LegalEntityListResponse) => {
        if (!res) {
          res = {};
        }
        res[`${legalEntity.id}`] = legalEntity.name;
        return res;
      }, {}),
    [legalEntities]
  );

  const partnersLookup = useMemo(
    () =>
      partners.reduce((res: any, partner: PartnerListResponse) => {
        if (!res) {
          res = {};
        }
        res[`${partner.id}`] = partner.name;
        return res;
      }, {}),
    [partners]
  );

  const warehousesLookup = useMemo(
    () =>
      warehouses.reduce((res: any, warehouse: any) => {
        if (!res) {
          res = {};
        }
        res[`${warehouse.id}`] = warehouse.name;
        return res;
      }, {}),
    [warehouses]
  );

  const [canRead] = usePermissions([AccessPermissions.CAN_READ_ORDER]);

  useEffect(() => {
    const { filterUsers } = cookiesService.getActOfAcceptanceFilter();
    setFilterUsers(
      userIds && users
        ? users.filter((u: UserListResponse) => userIds.includes(u.id)).map(userToLookup)
        : filterUsers ?? []
    );
  }, [userIds, users]);

  useEffect(() => {
    const { filterCompanies } = cookiesService.getActOfAcceptanceFilter();

    setFilterCompanies(
      companyIds && companies
        ? companies
            .filter((c: CompanyListResponse) => companyIds.includes(c.id))
            .map(companyToLookup)
        : filterCompanies ?? []
    );
  }, [companies, companyIds]);

  useEffect(() => {
    const { filterLegalEntities } = cookiesService.getActOfAcceptanceFilter();

    setFilterLegalEntities(
      legalEntityIds && legalEntities
        ? legalEntities
            .filter((le: LegalEntityListResponse) => legalEntityIds.includes(le.id))
            .map(legalEntityToLookup)
        : filterLegalEntities ?? []
    );
  }, [legalEntityIds, legalEntities]);

  useEffect(() => {
    const { filterPartners } = cookiesService.getActOfAcceptanceFilter();
    setFilterPartners(
      partnerIds && partners
        ? partners
            .filter((p: PartnerListResponse) => partnerIds.includes(p.id))
            .map(partnerToLookup)
        : filterPartners ?? []
    );
  }, [partners, partnerIds]);

  useEffect(() => {
    const { filterType } = cookiesService.getActOfAcceptanceFilter();

    setFilterType(
      typeIds ? orderTypeLookups.filter((ot: Lookup) => typeIds.includes(ot.id)) : filterType ?? []
    );
  }, [typeIds]);

  const onSearch = useCallback(() => {
    const fromDate = isValidDate(filterFromDate) ? new Date(toStartOfDay(filterFromDate!)) : null;
    const toDate = isValidDate(filterToDate) ? new Date(toEndOfDay(filterToDate!)) : null;
    cookiesService.setActOfAcceptanceFilter({
      filterFromDate: fromDate,
      filterToDate: toDate,
      filterUsers,
      filterCompanies,
      filterLegalEntities,
      filterPartners,
      filterType
    });
    let params: any = {};
    if (filterFromDate) {
      params.fromDate = toDateString(filterFromDate);
    }
    if (filterToDate) {
      params.toDate = toDateString(filterToDate);
    }
    if (filterCompanies?.length > 0) {
      params.companyIds = filterCompanies.map((c) => c.id).join(',');
    }
    if (filterLegalEntities?.length > 0) {
      params.legalEntitiesIds = filterLegalEntities.map((l) => l.id).join(',');
    }
    if (filterPartners?.length > 0) {
      params.partnerIds = filterPartners.map((p) => p.id).join(',');
    }
    if (filterUsers?.length > 0) {
      params.userIds = filterUsers.map((u) => u.id).join(',');
    }
    if (filterType?.length > 0) {
      params.typeIds = filterType.map((type) => type.id).join(',');
    }
    history.push({
      pathname: '/acts-of-acceptance',
      search: new URLSearchParams(params).toString()
    });
    setParameters([
      fromDate,
      toDate,
      filterPartners.map((p: Lookup) => p.id),
      filterLegalEntities.map((l: Lookup) => l.id),
      filterCompanies.map((c: Lookup) => c.id),
      filterUsers.map((u: Lookup) => u.id),
      filterType.map((t: Lookup) => t.id)
    ]);
  }, [
    filterFromDate,
    filterToDate,
    filterUsers,
    filterCompanies,
    filterLegalEntities,
    filterPartners,
    filterType,
    history,
    setParameters
  ]);

  const paymentOptionDistribution: any = useMemo(
    () =>
      actsOfAcceptance.reduce(
        (res: any, aoa: ActOfAcceptanceListResponse) => {
          const key =
            aoa.orderPaymentOption?.replace('Начин на плащане: <b>', '').replace('</b>', '') ??
            'Без';
          if (!res[aoa.currency][key]) {
            res[aoa.currency][key] = 0;
          }
          res[aoa.currency][key] = res[aoa.currency][key] + +aoa.orderTotalIncludingVat;
          return res;
        },
        { BGN: {}, EUR: {} }
      ),
    [actsOfAcceptance]
  );

  return (
    <Grid container spacing={1}>
      <Grid item lg={2} md={6} sm={12} xs={12}>
        <KeyboardDatePicker
          disableToolbar
          autoOk={true}
          variant="inline"
          format="dd/MM/yy"
          margin="dense"
          label="От"
          helperText={''}
          value={filterFromDate ? filterFromDate : null}
          onChange={(value: Date | null) => {
            if (value) {
              if (isValidDate(value)) {
                setFilterFromDate(ignoreOffset(value));
              }
            } else {
              setFilterFromDate(null);
            }
          }}
          inputVariant="outlined"
          fullWidth
          KeyboardButtonProps={{
            'aria-label': 'change date'
          }}
          maxDate={filterToDate}
        />
      </Grid>
      <Grid item lg={2} md={6} sm={12} xs={12}>
        <KeyboardDatePicker
          disableToolbar
          autoOk={true}
          variant="inline"
          format="dd/MM/yy"
          margin="dense"
          label="До"
          helperText={''}
          value={filterToDate ? filterToDate : null}
          onChange={(value: Date | null) => {
            if (value) {
              if (isValidDate(value)) {
                setFilterToDate(ignoreOffset(value));
              }
            } else {
              setFilterToDate(null);
            }
          }}
          inputVariant="outlined"
          fullWidth
          KeyboardButtonProps={{
            'aria-label': 'change date'
          }}
          minDate={filterFromDate}
        />
      </Grid>
      <Grid item lg={4} md={6} sm={12} xs={12}>
        <AMSAutocomplete
          label="Потребители"
          options={users.map(userToLookup)}
          value={filterUsers}
          onChange={setFilterUsers}
          minChar={0}
          disableCloseOnSelect
          renderOption={(option, { selected }) => (
            <Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                className={classes.checkbox}
                checked={selected}
                color="primary"
              />
              {option.value}
            </Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Потребители"
              placeholder=""
              margin="dense"
              fullWidth
            />
          )}
          multiple
          required
        />
      </Grid>
      <Grid item lg={4} md={6} sm={12} xs={12}>
        <AMSAutocomplete
          label="Юридически лица"
          options={legalEntities.map(legalEntityToLookup)}
          value={filterLegalEntities}
          onChange={setFilterLegalEntities}
          minChar={0}
          disableCloseOnSelect
          renderOption={(option, { selected }) => (
            <Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                className={classes.checkbox}
                checked={selected}
                color="primary"
              />
              {option.value}
            </Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Юридически лица"
              placeholder=""
              margin="dense"
              fullWidth
            />
          )}
          multiple
          required
        />
      </Grid>
      <Grid item lg={4} md={6} sm={12} xs={12}>
        <AMSAutocomplete
          label="Компании"
          options={companies.map(companyToLookup)}
          value={filterCompanies}
          onChange={setFilterCompanies}
          minChar={0}
          disableCloseOnSelect
          renderOption={(option, { selected }) => (
            <Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                className={classes.checkbox}
                checked={selected}
                color="primary"
              />
              {option.value}
            </Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Компании"
              placeholder=""
              margin="dense"
              fullWidth
            />
          )}
          multiple
          required
        />
      </Grid>

      <Grid item lg={4} md={6} sm={12} xs={12}>
        <AMSAutocomplete
          label="Получатели"
          options={partners.map(partnerToLookup)}
          value={filterPartners}
          onChange={setFilterPartners}
          minChar={0}
          disableCloseOnSelect
          renderOption={(option, { selected }) => (
            <Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                className={classes.checkbox}
                checked={selected}
                color="primary"
              />
              {option.value}
            </Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Получатели"
              placeholder=""
              margin="dense"
              fullWidth
            />
          )}
          multiple
          required
        />
      </Grid>
      <Grid item lg={3} md={6} sm={12} xs={12}>
        <AMSAutocomplete
          multiple
          minChar={0}
          limitTags={3}
          options={orderTypeLookups}
          sortOptions={false}
          disableCloseOnSelect
          renderOption={(option, { selected }) => (
            <Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                className={classes.checkbox}
                checked={selected}
                color="primary"
              />
              {option.value}
            </Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Тип"
              placeholder=""
              margin="dense"
              fullWidth
            />
          )}
          value={filterType}
          onChange={(values) => setFilterType(values)}
        />
      </Grid>
      <Grid item lg={1} md={1} sm={12} xs={12}>
        <AMSButton
          color="primary"
          variant="contained"
          text="Търсене"
          loading={loading}
          disabled={false}
          onClick={onSearch}
          style={{
            marginTop: 8
          }}
        />
      </Grid>
      <Grid item lg={12}>
        <AMSTable
          title={
            <Grid container spacing={2}>
              <Grid item lg={4}>
                <Typography style={{ float: 'left' }}>Списък с протоколи</Typography>
              </Grid>
              {paymentOptionDistribution && (
                <Grid item lg={8}>
                  <Grid container spacing={1}>
                    {['BGN', 'EUR'].map((currency: string) =>
                      Object.keys(paymentOptionDistribution[currency]).map((key: string) => (
                        <Fragment key={key}>
                          <Grid item lg={9}>
                            <Typography style={{ float: 'right' }}>{key}:</Typography>
                          </Grid>
                          <Grid item lg={3}>
                            <Typography style={{ float: 'right' }}>
                              {`${paymentOptionDistribution[currency][key].toFixed(2)}${
                                currency === 'EUR' ? ' €' : ' лв.'
                              }`}
                            </Typography>
                          </Grid>
                        </Fragment>
                      ))
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          }
          columns={[
            {
              title: '№',
              field: 'number',
              cellStyle: { width: '10%' },
              render: (rowData: ActOfAcceptanceListResponse) => (
                <AMSLink href={`/act-of-acceptance?id=${rowData.id}`}>{rowData.number}</AMSLink>
              )
            },
            { title: 'Дата на създаване', field: 'createdAt', cellStyle: { width: '10%' } },
            {
              title: 'Компания',
              field: 'companyId',
              cellStyle: { width: '15%' },
              lookup: companiesLookup
            },
            {
              title: 'Получател',
              field: 'receiverId',
              cellStyle: { width: '15%' },
              lookup: partnersLookup,
              render: (aoa: ActOfAcceptanceListResponse) => (
                <AMSLink href={`/partner?id=${aoa.receiverId}`}>
                  {partnersLookup && warehousesLookup && aoa.receiverId
                    ? aoa.orderType === OrderTypeKeys.INTERNAL
                      ? warehousesLookup[aoa.receiverId]
                      : partnersLookup[aoa.receiverId]
                    : 'Зареждане ...'}
                </AMSLink>
              )
            },
            {
              title: 'Юридическо лице',
              field: 'receiverLegalEntityId',
              cellStyle: { width: '15%' },
              lookup: legalEntitiesLookup,
              render: (aoa: ActOfAcceptanceListResponse) => (
                <AMSLink href={`/legal-entity?id=${aoa.receiverLegalEntityId}`}>
                  {legalEntitiesLookup && aoa.receiverLegalEntityId
                    ? legalEntitiesLookup[aoa.receiverLegalEntityId]
                    : 'Зареждане ...'}
                </AMSLink>
              )
            },
            {
              title: 'Потребител',
              field: 'createdById',
              cellStyle: { width: '15%' },
              lookup: usersLookup
            },
            {
              title: 'Тип на поръчката',
              field: 'orderType',
              cellStyle: { width: '15%' },
              render: (rowData: any) => getOrderTypeLabelByKey(rowData.orderType)
            },
            {
              title: 'Сума',
              field: 'orderTotalIncludingVat',
              cellStyle: { width: '15%' },
              align: 'right',
              render: (rowData: any) =>
                `${rowData.orderTotalIncludingVat ?? '0.00'}${
                  rowData.currency === 'EUR' ? ' €' : ' лв.'
                }`
            }
          ]}
          customAction={
            canRead
              ? {
                  icon: () => (
                    <DescriptionIcon
                      style={{
                        color: '#252525 !important'
                      }}
                    />
                  ),
                  tooltip: 'Преглед на документ',
                  onClick: (event: Event, actOfAcceptance: ActOfAcceptanceListResponse) => {
                    setTitle(actOfAcceptance.number);
                    setUrl(getActOfAcceptanceUrl(actOfAcceptance.id));
                    setOpenActOfAcceptancePreview(true);
                  }
                }
              : undefined
          }
          pageSize={10}
          search={true}
          data={actsOfAcceptance}
          isLoading={loading}
          exportFileName="Списък с протоколи"
        />
      </Grid>
      <AMSPdfViewerDialog
        title={title}
        url={url}
        open={openActOfAcceptancePreview}
        onClose={() => setOpenActOfAcceptancePreview(false)}
      />
    </Grid>
  );
};

export default ActsOfAcceptanceComponent;
